import { Editor } from '@tiptap/react';
import {
  BoldIcon,
  CodeIcon,
  Heading1Icon,
  Heading2Icon,
  ImageIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  StrikethroughIcon,
  VideoIcon,
} from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

interface MenuBarProps {
  editor: Editor;
  className?: string;
}

export function MenuBar({ editor, className }: MenuBarProps) {
  const [activeValues, setActiveValues] = React.useState<string[]>([]);

  React.useEffect(() => {
    const updateActiveValues = () => {
      const values: string[] = [];
      if (editor.isActive('bold')) values.push('bold');
      if (editor.isActive('italic')) values.push('italic');
      if (editor.isActive('strike')) values.push('strike');
      if (editor.isActive('code')) values.push('code');
      if (editor.isActive('heading', { level: 1 })) values.push('h1');
      if (editor.isActive('heading', { level: 2 })) values.push('h2');
      if (editor.isActive('bulletList')) values.push('bulletList');
      if (editor.isActive('orderedList')) values.push('orderedList');
      setActiveValues(values);
    };

    editor.on('selectionUpdate', updateActiveValues);
    editor.on('focus', updateActiveValues);
    editor.on('transaction', updateActiveValues);

    return () => {
      editor.off('selectionUpdate', updateActiveValues);
      editor.off('focus', updateActiveValues);
      editor.off('transaction', updateActiveValues);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  const handleValueChange = (values: string[]) => {
    const addedValue = values.find((v) => !activeValues.includes(v));
    const removedValue = activeValues.find((v) => !values.includes(v));
    const value = addedValue || removedValue;

    switch (value) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'strike':
        editor.chain().focus().toggleStrike().run();
        break;
      case 'code':
        editor.chain().focus().toggleCode().run();
        break;
      case 'h1':
        editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case 'h2':
        editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case 'bulletList':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'orderedList':
        editor.chain().focus().toggleOrderedList().run();
        break;
    }
  };

  return (
    <div className={cn('flex items-center justify-between gap-2', className)}>
      <ToggleGroup
        type="multiple"
        value={activeValues}
        onValueChange={handleValueChange}
        className="flex gap-1"
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
      >
        <ToggleGroupItem size="sm" value="bold" aria-label="Toggle bold">
          <BoldIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="italic" aria-label="Toggle italic">
          <ItalicIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="strike" aria-label="Toggle strikethrough">
          <StrikethroughIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="code" aria-label="Toggle code">
          <CodeIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="h1" aria-label="Toggle heading 1">
          <Heading1Icon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="h2" aria-label="Toggle heading 2">
          <Heading2Icon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="bulletList" aria-label="Toggle bullet list">
          <ListIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem size="sm" value="orderedList" aria-label="Toggle ordered list">
          <ListOrderedIcon className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Upload buttons */}
      <div className="flex items-center space-x-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = true;
            input.onchange = (e) => {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              if (files.length > 0) {
                // Additional validation since accept attribute is just a UI hint
                const validImageFiles = files.filter(file => file.type.startsWith('image/'));
                if (validImageFiles.length > 0) {
                  editor.commands.uploadImages(validImageFiles);
                }
              }
            };
            input.click();
          }}
          title="Upload image"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'video/*';
            input.multiple = true;
            input.onchange = (e) => {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              if (files.length > 0) {
                // Additional validation since accept attribute is just a UI hint
                const validVideoFiles = files.filter(file => file.type.startsWith('video/'));
                if (validVideoFiles.length > 0) {
                  editor.commands.uploadVideos(validVideoFiles);
                }
              }
            };
            input.click();
          }}
          title="Upload video"
        >
          <VideoIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
